/**
 * Product data utilities
 * Pure functions for product data transformation
 */
import { getAppConfig } from "../../../../utils/appConfig";

const { defaultImage } = getAppConfig();

/**
 * Normalize product data for consistent handling
 * Handles multilingual fields and ensures flat structure
 *
 * @param {Array} data - Array of products
 * @returns {Array} Normalized products
 */
export const normalizeProducts = (data) =>
  data.map((product) => {
    const productNameObj = product.productName || {};
    const descriptionObj = product.description || {};

    return {
      ...product,

      // preserve multilingual values
      _i18n: {
        productName: productNameObj,
        description: descriptionObj,
      },

      // flat values for table / search / sort
      productName:
        typeof productNameObj === "string"
          ? productNameObj
          : productNameObj.en || Object.values(productNameObj)[0] || "",

      description:
        typeof descriptionObj === "string"
          ? descriptionObj
          : descriptionObj.en || Object.values(descriptionObj)[0] || "",
    };
  });

/**
 * Filter products by search query
 * Searches across multiple fields
 *
 * @param {Array} products - Products array
 * @param {string} searchQuery - Search term
 * @returns {Array} Filtered products
 */
export const filterProducts = (products, searchQuery) => {
  if (!searchQuery) return products;

  return products.filter((product) => {
    const searchableFields = [
      product.productName,
      product.productNumber,
      product.description,
      product.categoryName,
      product.subcategoryName,
      product.status,
    ];

    return searchableFields.some(
      (field) =>
        field &&
        field.toString().toLowerCase().includes(searchQuery.toLowerCase()),
    );
  });
};

/**
 * Sort products by field and order
 *
 * @param {Array} products - Products array
 * @param {string} field - Field to sort by
 * @param {number} order - Sort order (1 for ascending, -1 for descending)
 * @returns {Array} Sorted products
 */
export const sortProducts = (products, field, order) => {
  if (!field) return products;

  return [...products].sort((a, b) => {
    const aValue = (a[field] || "").toString().toLowerCase();
    const bValue = (b[field] || "").toString().toLowerCase();

    if (aValue < bValue) return -1 * order;
    if (aValue > bValue) return 1 * order;
    return 0;
  });
};

/**
 * Paginate data array
 *
 * @param {Array} data - Data to paginate
 * @param {number} page - Current page (1-indexed)
 * @param {number} rowsPerPage - Rows per page
 * @returns {Array} Paginated data
 */
export const paginateData = (data, page, rowsPerPage) => {
  const startIndex = (page - 1) * rowsPerPage;
  return data.slice(startIndex, startIndex + rowsPerPage);
};

/**
 * Get image URL with fallback
 *
 * @param {Object} product - Product object
 * @param {string} baseUrl - API base URL
 * @returns {string} Image URL
 */
export const getProductImageUrl = (product, baseUrl) => {
  if (product.image || product.imageUrl) {
    return `${baseUrl}${product.image || product.imageUrl}`;
  }
  return defaultImage;
};
